import type { ConnectedPosition } from '@angular/cdk/overlay';
import type { OnDestroy } from '@angular/core';
import { Injectable } from '@angular/core';

import type {
  MentionElement,
  MentionElementGroup,
  MentionListController,
  MentionNavigationKey,
  OverlayPosition,
} from '@core/prosemirror-legacy';
import { WysiwygComponentPortalService } from '@core/prosemirror-legacy/angular';

import { GroupedMentionsComponent } from './grouped-mentions.component';

@Injectable()
export class GroupedMentionsListService
  extends WysiwygComponentPortalService<GroupedMentionsComponent>
  implements MentionListController, OnDestroy
{
  protected overlayConfig = {
    hasBackdrop: true,
    backdropClass: 'cdk-overlay-transparent-backdrop',
  };

  private restoreFocusCallback?: () => void;

  public open(
    position: OverlayPosition | DOMRect,
    _options: {
      withSearch?: boolean;
      preferredPositions?: ConnectedPosition[];
      queryChange?: (query: string) => void;
    },
  ) {
    const options = {
      withSearch: _options.withSearch || false,
      preferredPositions: _options.preferredPositions || [],
      queryChange: _options.queryChange || (() => void 0),
    };
    this.close();
    this.openComponent(GroupedMentionsComponent, position, options.preferredPositions);
    const { instance, location } = this.componentRef!;
    instance.searchEnabled = options.withSearch;
    instance.queryChange = options.queryChange;
    instance.close = () => {
      this.close();
      this.restoreFocusCallback?.();
    };
    return {
      element: <HTMLElement>location.nativeElement,
      backdropClick$: this.overlayRef!.backdropClick(),
    };
  }

  public setSelectCallback(selectCb: (val: MentionElement) => void): void {
    if (!this.componentRef) return;
    this.componentRef.instance.select = selectCb;
  }

  public setData(data: MentionElementGroup[]): void {
    if (!this.componentRef) return;

    this.componentRef.instance.groupedData = data;
    this.componentRef.instance.elCount = data.reduce((acc, group) => acc + group.data.length, 0);
    this.componentRef.changeDetectorRef.detectChanges();
    this.overlayRef!.updatePosition();
  }

  public handleKey(key: MentionNavigationKey) {
    this.componentRef?.instance.handleKey(key);
  }

  public registerRestoreFocus(restoreFocusCallback: () => void) {
    this.restoreFocusCallback = restoreFocusCallback;
  }
}
